import { Activity, memo, useContext, useEffect, useLayoutEffect, useState } from "react";
import {
  UNSAFE_RouteContext,
  useLocation,
  useNavigationType,
  useRoutes,
  type Location,
  type RouteObject,
} from "react-router-dom";
import { EMPTY_KEPT_PAGES, nextKeptPages, type KeptPagesState } from "../../lib/navigation/keptPages";
import { PageOwnerContext, clearPageOwnerStyle, showOnlyPageOwner } from "../../lib/navigation/pageOwner";

/**
 * KeepAliveOutlet — o `<Outlet />` do `AppFrame` que GUARDA as telas
 * (BUG-331, 06/10/2026).
 *
 * A tela de onde se saiu fica montada dentro de um `<Activity mode="hidden">`:
 * DOM, estado e imagens decodificadas ficam; os efeitos (assinaturas do
 * Firestore, timers) são desligados enquanto ela está escondida e religados
 * quando volta — o custo de rede é o mesmo de remontar. No voltar, a tela
 * aparece pronta e na altura certa, e o `ScrollToTop` acerta a posição no
 * primeiro quadro. Qual instância cada navegação usa: `lib/navigation/keptPages.ts`.
 *
 * ── Como cada tela enxerga a PRÓPRIA rota ──────────────────────────────────
 * As rotas filhas vêm do layout que nos renderiza (`UNSAFE_RouteContext`) e
 * cada instância roda `useRoutes(rotas, aLocationDela)`: a escondida segue
 * vendo o endereço dela — sem isso o `useParams` de uma ficha escondida leria o
 * livro da tela nova. Cada instância roda sob um contexto de rota RAIZ: o
 * layout não tem path, então casar a partir da raiz é o mesmo casamento, e o
 * React Router não acusa "descendant <Routes>" sem `*`.
 *
 * ⚠️ Portais da tela guardada: ver `lib/navigation/pageOwner.tsx` — o Activity
 * não os esconde; quem esconde é a marca de dono + CSS.
 * ⚠️ O `Suspense` continua FORA, no `AppFrame`: uma fronteira nova por tela
 * mostraria o loader em toda navegação para página `lazy()` ainda não baixada,
 * em vez de manter a tela de antes durante a transição.
 */

const ROOT_ROUTE_CONTEXT = { outlet: null, matches: [], isDataRoute: false };

const KeptPage = memo(function KeptPage({
  routes,
  location,
}: {
  routes: RouteObject[];
  location: Location;
}) {
  return useRoutes(routes, location);
});

export function KeepAliveOutlet() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { matches } = useContext(UNSAFE_RouteContext);
  const routes = (matches[matches.length - 1]?.route.children ?? []) as RouteObject[];

  // Estado ajustado DURANTE o render a partir do anterior (o padrão do React
  // para "guardar algo do render passado"): sem efeito intermediário, a troca
  // de tela sai no mesmo commit; e um render descartado pela concorrência
  // descarta o ajuste junto — nenhuma tela fantasma guardada.
  // `nextKeptPages` devolve o MESMO objeto quando nada mudou: sem laço.
  const [kept, setKept] = useState<KeptPagesState>(EMPTY_KEPT_PAGES);
  const state = nextKeptPages(kept, location, navigationType);
  if (state !== kept) setKept(state);

  useLayoutEffect(() => {
    // No mesmo commit da troca de tela, antes da pintura: o portal da tela que
    // saiu nunca chega a ser pintado por cima da que entrou.
    showOnlyPageOwner(location.pathname);
  }, [location.pathname]);
  useEffect(() => () => clearPageOwnerStyle(), []);

  return (
    <UNSAFE_RouteContext.Provider value={ROOT_ROUTE_CONTEXT}>
      {state.pages.map((page) => (
        <Activity key={page.id} mode={page.id === state.activeId ? "visible" : "hidden"}>
          <PageOwnerContext.Provider value={page.path}>
            <KeptPage routes={routes} location={page.location} />
          </PageOwnerContext.Provider>
        </Activity>
      ))}
    </UNSAFE_RouteContext.Provider>
  );
}
